/**
 * POS Product Management Page
 * ==========================
 * 
 * Product CRUD for the POS system - list, create, edit, delete products.
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Package,
  Plus,
  Search,
  Edit,
  Trash2,
  ArrowLeft,
  PoundSterling,
  Tag,
  Barcode,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Image,
  Upload,
  Camera,
  Eye,
  EyeOff,
  ToggleLeft,
  ToggleRight,
  Truck,
  Layers,
  Settings2,
  GripVertical,
  X,
} from "lucide-react";

const POSProductsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [viewMode, setViewMode] = useState("table"); // "table" or "grid"
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const limit = 20;
  
  // Modal state
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    sku: "",
    barcode: "",
    name: "",
    description: "",
    category_id: "",
    retail_price: "",
    cost_price: "",
    low_stock_threshold: "10",
    track_inventory: true,
    is_active: true,
    offers_delivery: false,
    image_url: "",
    // Generic product fields
    product_type: "product",
    has_variants: false,
    variant_label: "Option",
    variants: [],
    has_modifiers: false,
    modifier_label: "Add-on",
    modifiers: []
  });
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  
  // Image upload state
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Barcode scanner input ref
  const barcodeInputRef = useRef(null);
  
  // Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/pos/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };
  
  // Fetch products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}/pos/products?limit=${limit}&skip=${(page - 1) * limit}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (selectedCategory && selectedCategory !== "all") url += `&category_id=${selectedCategory}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
        // Estimate total for pagination (simplified)
        setTotalProducts(data.length === limit ? (page * limit) + 1 : (page - 1) * limit + data.length);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchCategories();
  }, []);
  
  useEffect(() => {
    fetchProducts();
  }, [page, searchQuery, selectedCategory]);
  
  const handleOpenProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        sku: product.sku || "",
        barcode: product.barcode || "",
        name: product.name || "",
        description: product.description || "",
        category_id: product.category_id || "",
        retail_price: product.retail_price ? (product.retail_price / 100).toFixed(2) : "",
        cost_price: product.cost_price ? (product.cost_price / 100).toFixed(2) : "",
        low_stock_threshold: product.low_stock_threshold?.toString() || "10",
        track_inventory: product.track_inventory !== false,
        is_active: product.is_active !== false,
        offers_delivery: product.offers_delivery === true,
        image_url: product.image_url || "",
        // Generic product fields
        product_type: product.product_type || "product",
        has_variants: product.has_variants || false,
        variant_label: product.variant_label || "Option",
        variants: (product.variants || []).map(v => ({
          ...v,
          price: v.price ? (v.price / 100).toFixed(2) : (v.retail_price ? (v.retail_price / 100).toFixed(2) : "")
        })),
        has_modifiers: product.has_modifiers || false,
        modifier_label: product.modifier_label || "Add-on",
        modifiers: (product.modifiers || []).map(m => ({
          ...m,
          price: m.price ? (m.price / 100).toFixed(2) : ""
        }))
      });
      setImagePreview(product.image_url ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}` : null);
    } else {
      setEditingProduct(null);
      setProductForm({
        sku: "",
        barcode: "",
        name: "",
        description: "",
        category_id: "",
        retail_price: "",
        cost_price: "",
        low_stock_threshold: "10",
        track_inventory: true,
        is_active: true,
        offers_delivery: false,
        image_url: "",
        // Generic product fields
        product_type: "product",
        has_variants: false,
        variant_label: "Option",
        variants: [],
        has_modifiers: false,
        modifier_label: "Add-on",
        modifiers: []
      });
      setImagePreview(null);
    }
    setShowProductModal(true);
  };
  
  // Image upload handler
  const handleImageUpload = async (e, productId = null) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid image type. Use PNG, JPEG, or WebP");
      return;
    }
    
    // Validate file size (1MB)
    if (file.size > 1 * 1024 * 1024) {
      toast.error("Image too large. Max 1MB");
      return;
    }
    
    setUploadingImage(true);
    
    try {
      // Upload using new file upload API
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await fetch(`${API}/merchant/upload/image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      
      if (!uploadRes.ok) {
        const err = await uploadRes.json();
        throw new Error(err.detail || "Upload failed");
      }
      
      const uploadData = await uploadRes.json();
      const imageUrl = uploadData.image_url;
      
      // Show preview
      setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${imageUrl}`);
      
      // If editing existing product, update the product
      if (productId) {
        const updateRes = await fetch(`${API}/pos/products/${productId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ image_url: imageUrl })
        });
        
        if (updateRes.ok) {
          toast.success("Image uploaded and saved");
          fetchProducts();
        }
      } else {
        // Store the image URL for when we save the new product
        setProductForm(prev => ({ ...prev, image_url: imageUrl }));
        toast.success("Image uploaded");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Handle barcode scanner input (typically sends keystrokes ending with Enter)
  const handleBarcodeScanner = async (barcode) => {
    if (!barcode) return;
    
    try {
      const res = await fetch(`${API}/pos/products/barcode/${barcode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const product = await res.json();
        toast.success(`Found: ${product.name}`);
        handleOpenProductModal(product);
      } else {
        toast.info(`Barcode ${barcode} not found. Create new product?`);
        setProductForm(prev => ({ ...prev, barcode }));
        setShowProductModal(true);
      }
    } catch (err) {
      console.error("Barcode lookup error:", err);
    }
  };
  
  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.sku || !productForm.retail_price) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSaving(true);
    try {
      // Process variants - convert prices to pence
      const processedVariants = productForm.variants.map((v, i) => ({
        name: v.name,
        price: v.price ? Math.round(parseFloat(v.price) * 100) : 0,
        cost_price: v.cost_price ? Math.round(parseFloat(v.cost_price) * 100) : null,
        is_default: v.is_default || i === 0,
        is_active: v.is_active !== false,
        sort_order: i
      }));
      
      // Process modifiers - convert prices to pence
      const processedModifiers = productForm.modifiers.map((m, i) => ({
        name: m.name,
        price: m.price ? Math.round(parseFloat(m.price) * 100) : 0,
        max_quantity: parseInt(m.max_quantity) || 1,
        is_active: m.is_active !== false,
        sort_order: i
      }));
      
      const payload = {
        sku: productForm.sku,
        barcode: productForm.barcode || null,
        name: productForm.name,
        description: productForm.description || null,
        category_id: productForm.category_id && productForm.category_id !== "none" ? productForm.category_id : null,
        retail_price: Math.round(parseFloat(productForm.retail_price) * 100),
        cost_price: productForm.cost_price ? Math.round(parseFloat(productForm.cost_price) * 100) : null,
        low_stock_threshold: parseInt(productForm.low_stock_threshold) || 10,
        track_inventory: productForm.track_inventory,
        is_active: productForm.is_active,
        offers_delivery: productForm.offers_delivery,
        image_url: productForm.image_url || null,
        // Generic product fields
        product_type: productForm.product_type,
        has_variants: productForm.has_variants && processedVariants.length > 0,
        variant_label: productForm.variant_label || "Option",
        variants: processedVariants,
        has_modifiers: productForm.has_modifiers && processedModifiers.length > 0,
        modifier_label: productForm.modifier_label || "Add-on",
        modifiers: processedModifiers
      };
      
      const url = editingProduct 
        ? `${API}/pos/products/${editingProduct.product_id}`
        : `${API}/pos/products`;
      
      const res = await fetch(url, {
        method: editingProduct ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(editingProduct ? "Product updated" : "Product created");
        setShowProductModal(false);
        fetchProducts();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to save product");
      }
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Failed to save product");
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteProduct = async () => {
    if (!productToDelete) return;
    
    try {
      const res = await fetch(`${API}/pos/products/${productToDelete.product_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Product deleted");
        setShowDeleteModal(false);
        setProductToDelete(null);
        fetchProducts();
      } else {
        toast.error("Failed to delete product");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  // Toggle product visibility (online/offline)
  const handleToggleVisibility = async (product) => {
    try {
      const res = await fetch(`${API}/products/${product.product_id}/toggle`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        // Update local state immediately for better UX
        setProducts(prev => prev.map(p => 
          p.product_id === product.product_id 
            ? { ...p, is_active: data.is_active }
            : p
        ));
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to toggle product");
      }
    } catch (error) {
      console.error("Error toggling product:", error);
      toast.error("Failed to toggle product visibility");
    }
  };
  
  const formatPrice = (pence) => {
    return `£${(pence / 100).toFixed(2)}`;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900">
      {/* Header - Brand Gradient */}
      <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/merchant" className="text-white/80 hover:text-white">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-xl font-semibold flex items-center gap-2 text-white">
                  <Package className="h-5 w-5 text-fuchsia-200" />
                  POS Products
                </h1>
                <p className="text-sm text-indigo-100">Manage your product catalog</p>
              </div>
            </div>
            <Button
              onClick={() => handleOpenProductModal()}
              className="bg-white text-indigo-700 hover:bg-indigo-50"
              data-testid="add-product-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="pl-10 bg-white border-gray-200"
                data-testid="product-search"
              />
            </div>
            <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setPage(1); }}>
              <SelectTrigger className="w-[180px] bg-white border-gray-200">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.category_id} value={cat.category_id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === "table" ? "default" : "outline"}
              size="icon"
              onClick={() => setViewMode("table")}
              className={viewMode === "table" ? "bg-indigo-600 text-white" : "border-gray-200"}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              size="icon"
              onClick={() => setViewMode("grid")}
              className={viewMode === "grid" ? "bg-indigo-600 text-white" : "border-gray-200"}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Product List */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 bg-white/50 rounded-xl border border-gray-200">
            <Package className="h-12 w-12 mx-auto text-gray-500 mb-4" />
            <h3 className="text-lg font-medium mb-2">No products yet</h3>
            <p className="text-gray-400 mb-4">Add your first product to start selling</p>
            <Button onClick={() => handleOpenProductModal()} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </div>
        ) : viewMode === "table" ? (
          <div className="bg-white/50 rounded-xl border border-gray-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="text-gray-400">Product</TableHead>
                  <TableHead className="text-gray-400">SKU</TableHead>
                  <TableHead className="text-gray-400">Category</TableHead>
                  <TableHead className="text-gray-400 text-right">Price</TableHead>
                  <TableHead className="text-gray-400 text-right">Stock</TableHead>
                  <TableHead className="text-gray-400">Status</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.product_id} className="border-gray-200" data-testid={`product-row-${product.product_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          {product.images?.[0]?.url ? (
                            <img src={product.images[0].url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <Package className="h-5 w-5 text-gray-500" />
                          )}
                        </div>
                        <span className="font-medium">{product.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-400">{product.sku}</TableCell>
                    <TableCell className="text-gray-400">{product.category_name || "-"}</TableCell>
                    <TableCell className="text-right font-medium">{formatPrice(product.retail_price)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`${product.total_stock <= product.low_stock_threshold ? "text-amber-400" : "text-slate-300"}`}>
                        {product.total_stock}
                        {product.total_stock <= product.low_stock_threshold && (
                          <AlertTriangle className="h-3 w-3 inline ml-1" />
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={product.is_active}
                          onCheckedChange={() => handleToggleVisibility(product)}
                          className="data-[state=checked]:bg-emerald-500"
                          data-testid={`toggle-product-${product.product_id}`}
                        />
                        <span className={`text-xs ${product.is_active ? "text-emerald-500" : "text-gray-400"}`}>
                          {product.is_active ? "Live" : "Offline"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenProductModal(product)}
                          className="text-gray-400 hover:text-indigo-500"
                          data-testid={`edit-product-${product.product_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setProductToDelete(product); setShowDeleteModal(true); }}
                          className="text-gray-400 hover:text-red-400"
                          data-testid={`delete-product-${product.product_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {products.map((product) => (
              <div
                key={product.product_id}
                className="bg-white/50 rounded-xl border border-gray-200 p-4 hover:border-indigo-500/50 transition-colors"
                data-testid={`product-card-${product.product_id}`}
              >
                <div className="aspect-square rounded-lg bg-gray-100 flex items-center justify-center mb-3 overflow-hidden">
                  {product.image_url ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${product.image_url}`} 
                      alt={product.name} 
                      className="w-full h-full object-cover" 
                    />
                  ) : product.images?.[0]?.url ? (
                    <img 
                      src={product.images[0].url.startsWith("http") ? product.images[0].url : `${process.env.REACT_APP_BACKEND_URL}${product.images[0].url}`} 
                      alt={product.name} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <Package className="h-8 w-8 text-gray-500" />
                  )}
                </div>
                <h3 className="font-medium text-sm truncate">{product.name}</h3>
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  {product.sku}
                  {product.barcode && (
                    <span className="text-gray-500" title={product.barcode}>
                      <Barcode className="h-3 w-3 inline" />
                    </span>
                  )}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-semibold text-indigo-400">{formatPrice(product.retail_price)}</span>
                  <span className={`text-xs ${product.total_stock <= product.low_stock_threshold ? "text-amber-400" : "text-gray-400"}`}>
                    {product.total_stock} in stock
                  </span>
                </div>
                
                {/* Visibility Toggle */}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                  <span className="text-xs text-gray-500">Store visibility</span>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={product.is_active}
                      onCheckedChange={() => handleToggleVisibility(product)}
                      className="data-[state=checked]:bg-emerald-500 scale-75"
                      data-testid={`toggle-grid-${product.product_id}`}
                    />
                    <span className={`text-xs ${product.is_active ? "text-emerald-500" : "text-gray-400"}`}>
                      {product.is_active ? "Live" : "Offline"}
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenProductModal(product)}
                    className="flex-1 border-gray-200 text-xs"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => { setProductToDelete(product); setShowDeleteModal(true); }}
                    className="border-gray-200 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {products.length > 0 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="border-gray-200"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-4 py-2 text-sm text-gray-400">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={products.length < limit}
              onClick={() => setPage(p => p + 1)}
              className="border-gray-200"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      
      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="bg-white border-gray-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Image Upload Section */}
            <div>
              <Label>Product Image</Label>
              <div className="mt-2 flex items-start gap-4">
                <div className="w-24 h-24 rounded-lg bg-gray-100 border border-dashed border-white/20 flex items-center justify-center overflow-hidden">
                  {imagePreview ? (
                    <img 
                      src={imagePreview.startsWith("data:") ? imagePreview : `${process.env.REACT_APP_BACKEND_URL}${imagePreview}`} 
                      alt="Preview" 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <Package className="h-8 w-8 text-gray-500" />
                  )}
                </div>
                <div className="flex-1">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, editingProduct?.product_id)}
                      data-testid="product-image-input"
                    />
                    <div className="flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300">
                      <Upload className="h-4 w-4" />
                      {uploadingImage ? "Uploading..." : "Upload Image"}
                    </div>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPEG, or WebP. Max 5MB.</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SKU *</Label>
                <Input
                  value={productForm.sku}
                  onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                  placeholder="SKU001"
                  className="bg-gray-100 border-gray-200"
                  data-testid="product-sku-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Barcode className="h-3 w-3" /> Barcode
                </Label>
                <Input
                  value={productForm.barcode}
                  onChange={(e) => setProductForm({ ...productForm, barcode: e.target.value })}
                  placeholder="Scan or enter barcode"
                  className="bg-gray-100 border-gray-200"
                  ref={barcodeInputRef}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && productForm.barcode) {
                      handleBarcodeScanner(productForm.barcode);
                    }
                  }}
                  data-testid="product-barcode-input"
                />
              </div>
            </div>
            <div>
              <Label>Product Name *</Label>
              <Input
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                placeholder="Product name"
                className="bg-gray-100 border-gray-200"
                data-testid="product-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                placeholder="Optional description"
                className="bg-gray-100 border-gray-200"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={productForm.category_id}
                onValueChange={(v) => setProductForm({ ...productForm, category_id: v })}
              >
                <SelectTrigger className="bg-gray-100 border-gray-200">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Retail Price (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={productForm.retail_price}
                  onChange={(e) => setProductForm({ ...productForm, retail_price: e.target.value })}
                  placeholder="19.99"
                  className="bg-gray-100 border-gray-200"
                  data-testid="product-price-input"
                />
              </div>
              <div>
                <Label>Cost Price (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={productForm.cost_price}
                  onChange={(e) => setProductForm({ ...productForm, cost_price: e.target.value })}
                  placeholder="Optional"
                  className="bg-gray-100 border-gray-200"
                />
              </div>
            </div>
            <div>
              <Label>Low Stock Alert Threshold</Label>
              <Input
                type="number"
                min="0"
                value={productForm.low_stock_threshold}
                onChange={(e) => setProductForm({ ...productForm, low_stock_threshold: e.target.value })}
                className="bg-gray-100 border-gray-200"
              />
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={productForm.track_inventory}
                  onChange={(e) => setProductForm({ ...productForm, track_inventory: e.target.checked })}
                  className="rounded bg-gray-100 border-gray-200"
                />
                <span className="text-sm">Track inventory</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={productForm.is_active}
                  onChange={(e) => setProductForm({ ...productForm, is_active: e.target.checked })}
                  className="rounded bg-gray-100 border-gray-200"
                />
                <span className="text-sm">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="offers-delivery-toggle">
                <input
                  type="checkbox"
                  checked={productForm.offers_delivery}
                  onChange={(e) => setProductForm({ ...productForm, offers_delivery: e.target.checked })}
                  className="rounded bg-gray-100 border-gray-200"
                />
                <span className="text-sm flex items-center gap-1">
                  <Truck className="h-3.5 w-3.5" />
                  Offer Delivery
                </span>
              </label>
            </div>
            
            {/* Product Type */}
            <div>
              <Label>Product Type</Label>
              <Select
                value={productForm.product_type}
                onValueChange={(v) => setProductForm({ ...productForm, product_type: v })}
              >
                <SelectTrigger className="bg-gray-100 border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">Product</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Variants Section */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-indigo-500" />
                  <Label className="text-sm font-medium">Variants</Label>
                  <span className="text-xs text-gray-500">(e.g., Size, Duration, Package)</span>
                </div>
                <Switch
                  checked={productForm.has_variants}
                  onCheckedChange={(checked) => setProductForm({ ...productForm, has_variants: checked })}
                  data-testid="has-variants-toggle"
                />
              </div>
              
              {productForm.has_variants && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Variant Label (shown to customers)</Label>
                    <Input
                      value={productForm.variant_label}
                      onChange={(e) => setProductForm({ ...productForm, variant_label: e.target.value })}
                      placeholder="e.g., Size, Duration, Package"
                      className="bg-gray-50 border-gray-200 h-8 text-sm"
                    />
                  </div>
                  
                  {/* Variant List */}
                  <div className="space-y-2">
                    {productForm.variants.map((variant, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <GripVertical className="h-4 w-4 text-gray-400" />
                        <Input
                          value={variant.name}
                          onChange={(e) => {
                            const newVariants = [...productForm.variants];
                            newVariants[idx] = { ...newVariants[idx], name: e.target.value };
                            setProductForm({ ...productForm, variants: newVariants });
                          }}
                          placeholder="Name (e.g., Large)"
                          className="flex-1 bg-white border-gray-200 h-8 text-sm"
                        />
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500 text-sm">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={variant.price}
                            onChange={(e) => {
                              const newVariants = [...productForm.variants];
                              newVariants[idx] = { ...newVariants[idx], price: e.target.value };
                              setProductForm({ ...productForm, variants: newVariants });
                            }}
                            placeholder="Price"
                            className="w-20 bg-white border-gray-200 h-8 text-sm"
                          />
                        </div>
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="radio"
                            name="default_variant"
                            checked={variant.is_default}
                            onChange={() => {
                              const newVariants = productForm.variants.map((v, i) => ({
                                ...v,
                                is_default: i === idx
                              }));
                              setProductForm({ ...productForm, variants: newVariants });
                            }}
                          />
                          Default
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const newVariants = productForm.variants.filter((_, i) => i !== idx);
                            setProductForm({ ...productForm, variants: newVariants });
                          }}
                          className="p-1 text-red-400 hover:text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setProductForm({
                        ...productForm,
                        variants: [...productForm.variants, { name: "", price: "", is_default: productForm.variants.length === 0 }]
                      });
                    }}
                    className="w-full border-dashed border-gray-300"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Variant
                  </Button>
                </div>
              )}
            </div>
            
            {/* Modifiers/Add-ons Section */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-emerald-500" />
                  <Label className="text-sm font-medium">Modifiers / Add-ons</Label>
                  <span className="text-xs text-gray-500">(e.g., Toppings, Extras)</span>
                </div>
                <Switch
                  checked={productForm.has_modifiers}
                  onCheckedChange={(checked) => setProductForm({ ...productForm, has_modifiers: checked })}
                  data-testid="has-modifiers-toggle"
                />
              </div>
              
              {productForm.has_modifiers && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Modifier Label (shown to customers)</Label>
                    <Input
                      value={productForm.modifier_label}
                      onChange={(e) => setProductForm({ ...productForm, modifier_label: e.target.value })}
                      placeholder="e.g., Toppings, Add-ons, Extras"
                      className="bg-gray-50 border-gray-200 h-8 text-sm"
                    />
                  </div>
                  
                  {/* Modifier List */}
                  <div className="space-y-2">
                    {productForm.modifiers.map((modifier, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <GripVertical className="h-4 w-4 text-gray-400" />
                        <Input
                          value={modifier.name}
                          onChange={(e) => {
                            const newModifiers = [...productForm.modifiers];
                            newModifiers[idx] = { ...newModifiers[idx], name: e.target.value };
                            setProductForm({ ...productForm, modifiers: newModifiers });
                          }}
                          placeholder="Name (e.g., Extra Cheese)"
                          className="flex-1 bg-white border-gray-200 h-8 text-sm"
                        />
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500 text-sm">+£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={modifier.price}
                            onChange={(e) => {
                              const newModifiers = [...productForm.modifiers];
                              newModifiers[idx] = { ...newModifiers[idx], price: e.target.value };
                              setProductForm({ ...productForm, modifiers: newModifiers });
                            }}
                            placeholder="Price"
                            className="w-20 bg-white border-gray-200 h-8 text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500 text-xs">Max:</span>
                          <Input
                            type="number"
                            min="1"
                            value={modifier.max_quantity || 1}
                            onChange={(e) => {
                              const newModifiers = [...productForm.modifiers];
                              newModifiers[idx] = { ...newModifiers[idx], max_quantity: e.target.value };
                              setProductForm({ ...productForm, modifiers: newModifiers });
                            }}
                            className="w-14 bg-white border-gray-200 h-8 text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newModifiers = productForm.modifiers.filter((_, i) => i !== idx);
                            setProductForm({ ...productForm, modifiers: newModifiers });
                          }}
                          className="p-1 text-red-400 hover:text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setProductForm({
                        ...productForm,
                        modifiers: [...productForm.modifiers, { name: "", price: "", max_quantity: 1 }]
                      });
                    }}
                    className="w-full border-dashed border-gray-300"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Modifier
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductModal(false)} className="border-gray-200">
              Cancel
            </Button>
            <Button
              onClick={handleSaveProduct}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-product-btn"
            >
              {saving ? "Saving..." : "Save Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-white border-gray-200 max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Product</DialogTitle>
          </DialogHeader>
          <p className="text-gray-400">
            Are you sure you want to delete "{productToDelete?.name}"? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="border-gray-200">
              Cancel
            </Button>
            <Button onClick={handleDeleteProduct} className="bg-red-600 hover:bg-red-700">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSProductsPage;
